import { rgba } from 'polished';
import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';

import { useScoreAnalytics } from '@treams/treams-analytics';

import {
	ScoreCategory,
	type ScoreColors,
	type TreamsCategoryScore,
	type TreamsScore,
	type UseTreamsScore,
} from '../TreamsScore.types';

const EMPTY_SCORE: TreamsCategoryScore = {
	score: 0,
	delta: 0,
};

const EMPTY_SCORES: TreamsScore = {
	[ScoreCategory.Targets]: EMPTY_SCORE,
	[ScoreCategory.Engagement]: EMPTY_SCORE,
	[ScoreCategory.Feedback]: EMPTY_SCORE,
	[ScoreCategory.OneOnOne]: EMPTY_SCORE,
	[ScoreCategory.Conversations]: EMPTY_SCORE,
	total: EMPTY_SCORE,
};

export const useScoreColors = (): ((score: number) => ScoreColors) => {
	const theme = useTheme();

	return useCallback(
		(score: number): ScoreColors => {
			if (score >= 8) {
				return {
					background: theme.colors.green,
					color: theme.colors.greenDark,
				};
			}

			if (score >= 7) {
				return {
					background: rgba(theme.colors.green, 0.45),
					color: theme.colors.greenDark,
				};
			}

			if (score >= 6) {
				return {
					background: rgba(theme.colors.green, 0.18),
					color: theme.colors.greenDark,
				};
			}

			if (score >= 5) {
				return {
					background: rgba(theme.colors.warning, 0.18),
					color: theme.colors.warning,
				};
			}

			return {
				background: rgba(theme.colors.red, 0.12),
				color: theme.colors.red,
			};
		},
		[theme.colors],
	);
};

export const useTreamsScore = (endDate: Date): UseTreamsScore => {
	const { scoreAnalytics } = useScoreAnalytics(endDate);

	return useMemo<UseTreamsScore>(
		() => ({
			isLoading: scoreAnalytics === null,
			rows: scoreAnalytics?.rows ?? [],
			scores: scoreAnalytics?.scores ?? EMPTY_SCORES,
			engagementSurveyAvailable: scoreAnalytics?.engagementSurveyAvailable ?? false,
			engagementFirstSurveyAt: scoreAnalytics?.engagementFirstSurveyAt ?? null,
		}),
		[scoreAnalytics],
	);
};
